import { useCallback, useEffect, useRef, useState } from "react";
import type { CommandEnvelope, RoomCommand, RoomProjection, ServerMessage } from "@quizshow/protocol";
import { issueSocketTicket } from "../api";

export function useRoomConnection(roomId: string | undefined, token: string | undefined) {
  const [projection, setProjection] = useState<RoomProjection>();
  const [status, setStatus] = useState<"connecting" | "online" | "offline">("connecting");
  const [error, setError] = useState<string>();
  const socketRef = useRef<WebSocket | undefined>(undefined);
  const revisionRef = useRef(0);

  useEffect(() => {
    if (!roomId || !token) return;
    let stopped = false;
    let reconnectTimer: number | undefined;
    let attempt = 0;

    const connect = async () => {
      try {
        setStatus("connecting");
        const { ticket } = await issueSocketTicket(roomId, token, revisionRef.current);
        if (stopped) return;
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const socket = new WebSocket(
          `${protocol}//${window.location.host}/api/rooms/${encodeURIComponent(roomId)}/ws?ticket=${encodeURIComponent(ticket)}`,
        );
        socketRef.current = socket;
        socket.onopen = () => {
          attempt = 0;
          setStatus("online");
          setError(undefined);
        };
        socket.onmessage = (event) => {
          const message = JSON.parse(String(event.data)) as ServerMessage;
          if (message.type === "snapshot") {
            revisionRef.current = message.projection.revision;
            setProjection(message.projection);
            setError(undefined);
          } else if (message.type === "command_result" && !message.accepted) {
            setError(message.error);
          } else if (message.type === "error") {
            setError(message.error);
          } else if (message.type === "room_closing") {
            setError("Der Raum wurde beendet.");
          }
        };
        socket.onclose = () => {
          if (stopped) return;
          setStatus("offline");
          attempt += 1;
          const delay = Math.min(10_000, 500 * 2 ** Math.min(attempt, 5)) + Math.floor(Math.random() * 300);
          reconnectTimer = window.setTimeout(() => void connect(), delay);
        };
        socket.onerror = () => socket.close();
      } catch (connectionError) {
        if (stopped) return;
        setStatus("offline");
        setError(connectionError instanceof Error ? connectionError.message : "Verbindung fehlgeschlagen.");
        attempt += 1;
        reconnectTimer = window.setTimeout(() => void connect(), Math.min(10_000, 800 * 2 ** attempt));
      }
    };

    void connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socketRef.current?.close();
    };
  }, [roomId, token]);

  const sendCommand = useCallback((command: RoomCommand) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError("Keine Live-Verbindung. Die Aktion wurde nicht gesendet.");
      return;
    }
    const envelope: CommandEnvelope = {
      type: "command",
      commandId: crypto.randomUUID(),
      expectedRevision: revisionRef.current,
      command,
    };
    socket.send(JSON.stringify(envelope));
  }, []);

  return { projection, status, error, setError, sendCommand };
}
